import React, { useContext, useState, useRef } from 'react';
import { BuildingContext } from '../../context/BuildingContext';
import { HexColorPicker } from 'react-colorful';
import '../../styles/components/ControlCenter.css';

// Shared UI component for number input
const NumberControl = ({ label, field, min, max, value, handleChange }) => (
  <div className="compact-control">
    <label>{label}:
      <input
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={handleChange(field)}
      />
    </label>
  </div>
);

// Shared UI component for toggle button
const ToggleButton = ({ field, onText, offText, className = "", handleToggle, isActive }) => (
  <button 
    onClick={handleToggle(field)}
    className={`feature-toggle ${isActive ? 'active' : ''} ${className}`}
  >
    {isActive ? onText : offText}
  </button>
);

// Shared UI component for color picker
const ColorControl = ({ label, field, value, activeColorField, toggleColorPicker, colorPickerRef, handleColorChange }) => (
  <div className="compact-color-control">
    <div className="color-label-swatch">
      <span className="color-label">{label}</span>
      <div 
        className="color-swatch"
        style={{ backgroundColor: value }}
        onClick={() => toggleColorPicker(field)}
      />
    </div>
    {activeColorField === field && (
      <div className="color-picker-popover" ref={colorPickerRef}>
        <HexColorPicker color={value} onChange={handleColorChange} />
        <div className="color-picker-cover" onClick={() => toggleColorPicker(null)} />
      </div>
    )}
  </div>
);

// Helper function to extract colors from a 32x32 pixel image
const extractColorsFromImage = (imageFile, callback) => {
  const reader = new FileReader();
  reader.onload = (event) => {
    const img = new Image();
    img.onload = () => {
      // Create a canvas to draw the image - no scaling needed for 32x32 images
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      // Extract pixel data
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const pixels = imageData.data;
      
      // Log canvas size for debugging
      console.log(`Processing image: ${canvas.width}x${canvas.height}`);
      
      // Collect unique colors with their frequencies
      const colors = {};
      for (let i = 0; i < pixels.length; i += 4) {
        if (pixels[i+3] < 128) continue; // Skip transparent pixels
        
        // Convert to hex
        const hex = rgbToHex(pixels[i], pixels[i+1], pixels[i+2]);
        if (!colors[hex]) colors[hex] = 0;
        colors[hex]++;
      }
      
      // Sort by frequency and take top colors
      const palette = Object.keys(colors)
        .sort((a, b) => colors[b] - colors[a])
        .slice(0, 10); // Get up to 10 dominant colors
      
      console.log('Extracted palette:', palette);
      callback(palette);
    };
    img.src = event.target.result;
  };
  reader.readAsDataURL(imageFile);
};

// Helper to convert RGB to HEX
const rgbToHex = (r, g, b) => {
  return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

const BuildingControlsRight = React.memo(({ 
  handleChange, 
  handleToggle, 
  handleColorChange, 
  handleRegenerateColors,
  activeColorField,
  setActiveColorField 
}) => {
  const { state } = useContext(BuildingContext);
  const colorPickerRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Local state for collapsing each section
  const [generateSchemeOpen, setGenerateSchemeOpen] = useState(true);
  const [colorOpen, setColorOpen] = useState(true);
  const [extrasOpen, setExtrasOpen] = useState(true);
  
  // Local state for color subsections
  const [buildingColorsOpen, setBuildingColorsOpen] = useState(true);
  const [facadeColorsOpen, setFacadeColorsOpen] = useState(true);
  const [groundFloorColorsOpen, setGroundFloorColorsOpen] = useState(true);
  const [windowColorsOpen, setWindowColorsOpen] = useState(true);
  
  // Local state for image preview
  const [imagePreview, setImagePreview] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Toggle color picker visibility
  const toggleColorPicker = (field) => {
    setActiveColorField(current => current === field ? null : field);
  };
  
  // Handle file selection for Nounish Colors
  const handleFileChange = (e) => {
    if (!e.target.files || e.target.files.length === 0) {
      return;
    }
    
    const file = e.target.files[0];
    setIsProcessing(true);
    
    // Create a preview
    const objectUrl = URL.createObjectURL(file);
    setImagePreview(objectUrl);
    
    // Process the image colors
    extractColorsFromImage(file, (palette) => {
      // Apply the extracted color palette to the building
      console.log('Applying Nounish color palette to building:', palette);
      handleRegenerateColors(palette);
      setIsProcessing(false);
    });
    
    // Clean up
    return () => URL.revokeObjectURL(objectUrl);
  };
  
  // Trigger file input click
  const handleNounishColorsClick = () => {
    fileInputRef.current.click();
  };

  return (
    <div className="ControlColumn right-column">
      {/* Generate Scheme Section */}
      <div className="controlSection">
        <h3 onClick={() => setGenerateSchemeOpen(prev => !prev)} className="section-title">
          {generateSchemeOpen ? '▾' : '▸'} Palette
        </h3>
        {generateSchemeOpen && (
          <div className="vertical-controls">
            <div className="scheme-buttons">
              <button 
                onClick={() => handleRegenerateColors(null)}
                className="scheme-button random-colors-button"
                title="Generate a random color scheme"
              >
                Generate Random 🎨
              </button>
              
              <button 
                onClick={handleNounishColorsClick}
                className="scheme-button nounish-colors-button"
                title="Generate colors from a 32x32 pixel image"
                disabled={isProcessing}
              >
                {isProcessing ? 'Processing... 🔄' : 'Nounish Colors 🖼️'}
              </button>
              
              <div className="upload-note">
                Upload your 32×32 noun PNG here
              </div>
              
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                accept="image/*"
                style={{ display: 'none' }}
              />
            </div>
            
            {imagePreview && (
              <div className="image-preview-container">
                <div className="image-info">
                  {isProcessing ? 'Extracting colors...' : 'Extracted colors from:'}
                </div>
                <img 
                  src={imagePreview} 
                  alt="Color source" 
                  className="image-preview" 
                />
                <div className="image-pixel-info">
                  Ideal size: 32×32 pixels
                </div>
                <button 
                  className="clear-image-button"
                  onClick={() => setImagePreview(null)}
                  title="Clear image"
                >
                  ✕
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    
      {/* Color Settings */}
      <div className="controlSection">
        <h3 onClick={() => setColorOpen(prev => !prev)} className="section-title">
          {colorOpen ? '▾' : '▸'} Colors
        </h3>
        {colorOpen && (
          <div className="vertical-controls">
            
            {/* Building Colors Subsection */}
            <div className="color-subsection">
              <h4 onClick={() => setBuildingColorsOpen(prev => !prev)} className="subsection-title">
                {buildingColorsOpen ? '▾' : '▸'} Building
              </h4>
              {buildingColorsOpen && (
                <div className="color-controls-grid">
                  <ColorControl 
                    label="Side Wall" 
                    field="sideWallColor" 
                    value={state.sideWallColor}
                    activeColorField={activeColorField}
                    toggleColorPicker={toggleColorPicker}
                    colorPickerRef={colorPickerRef}
                    handleColorChange={handleColorChange}
                  />
                  <ColorControl 
                    label="Level Lines" 
                    field="levelLinesColor" 
                    value={state.levelLinesColor}
                    activeColorField={activeColorField}
                    toggleColorPicker={toggleColorPicker}
                    colorPickerRef={colorPickerRef}
                    handleColorChange={handleColorChange}
                  />
                  <ColorControl 
                    label="Roof" 
                    field="roofColor" 
                    value={state.roofColor}
                    activeColorField={activeColorField}
                    toggleColorPicker={toggleColorPicker}
                    colorPickerRef={colorPickerRef}
                    handleColorChange={handleColorChange}
                  />
                  <ColorControl 
                    label="Guardrail" 
                    field="guardrailColor" 
                    value={state.guardrailColor}
                    activeColorField={activeColorField}
                    toggleColorPicker={toggleColorPicker}
                    colorPickerRef={colorPickerRef}
                    handleColorChange={handleColorChange}
                  />
                  <ColorControl 
                    label="Roof Access" 
                    field="roofAccessColor" 
                    value={state.roofAccessColor}
                    activeColorField={activeColorField}
                    toggleColorPicker={toggleColorPicker}
                    colorPickerRef={colorPickerRef}
                    handleColorChange={handleColorChange}
                  />
                </div>
              )}
            </div>

            {/* Facade Features Subsection */}
            <div className="color-subsection">
              <h4 onClick={() => setFacadeColorsOpen(prev => !prev)} className="subsection-title">
                {facadeColorsOpen ? '▾' : '▸'} Facade Features
              </h4>
              {facadeColorsOpen && (
                <div className="color-controls-grid">
                  <ColorControl 
                    label="Facade" 
                    field="facadeColor" 
                    value={state.facadeColor}
                    activeColorField={activeColorField}
                    toggleColorPicker={toggleColorPicker}
                    colorPickerRef={colorPickerRef}
                    handleColorChange={handleColorChange}
                  />
                   <ColorControl 
                    label="Window A" 
                    field="WindowColorA" 
                    value={state.WindowColorA}
                    activeColorField={activeColorField}
                    toggleColorPicker={toggleColorPicker}
                    colorPickerRef={colorPickerRef}
                    handleColorChange={handleColorChange}
                  />
                  <ColorControl 
                    label="Window B" 
                    field="WindowColorB" 
                    value={state.WindowColorB}
                    activeColorField={activeColorField}
                    toggleColorPicker={toggleColorPicker}
                    colorPickerRef={colorPickerRef}
                    handleColorChange={handleColorChange}
                  />
                  <ColorControl 
                    label="Window Frame" 
                    field="windowFrameColor" 
                    value={state.windowFrameColor}
                    activeColorField={activeColorField}
                    toggleColorPicker={toggleColorPicker}
                    colorPickerRef={colorPickerRef}
                    handleColorChange={handleColorChange}
                  />
                  <ColorControl 
                    label="Balcony" 
                    field="balconyColor" 
                    value={state.balconyColor}
                    activeColorField={activeColorField}
                    toggleColorPicker={toggleColorPicker}
                    colorPickerRef={colorPickerRef}
                    handleColorChange={handleColorChange}
                  />
                  <ColorControl 
                    label="Awning" 
                    field="awningColor" 
                    value={state.awningColor}
                    activeColorField={activeColorField}
                    toggleColorPicker={toggleColorPicker}
                    colorPickerRef={colorPickerRef}
                    handleColorChange={handleColorChange}
                  />
                </div>
              )}
            </div>

            {/* Ground Floor Subsection */}
            <div className="color-subsection">
              <h4 onClick={() => setGroundFloorColorsOpen(prev => !prev)} className="subsection-title">
                {groundFloorColorsOpen ? '▾' : '▸'} Ground Floor
              </h4>
              {groundFloorColorsOpen && (
                <div className="color-controls-grid">
                  <ColorControl 
                    label="Ground Floor" 
                    field="groundFloorColor" 
                    value={state.groundFloorColor}
                    activeColorField={activeColorField}
                    toggleColorPicker={toggleColorPicker}
                    colorPickerRef={colorPickerRef}
                    handleColorChange={handleColorChange}
                  />
                  <ColorControl 
                    label="Vitrine" 
                    field="vitrineColor" 
                    value={state.vitrineColor}
                    activeColorField={activeColorField}
                    toggleColorPicker={toggleColorPicker}
                    colorPickerRef={colorPickerRef}
                    handleColorChange={handleColorChange}
                  />
                  <ColorControl 
                    label="Vitrine Frame" 
                    field="vitrineFrameColor" 
                    value={state.vitrineFrameColor}
                    activeColorField={activeColorField}
                    toggleColorPicker={toggleColorPicker}
                    colorPickerRef={colorPickerRef}
                    handleColorChange={handleColorChange}
                  />
                  <ColorControl 
                    label="Vitrine Awning" 
                    field="vitrineAwningColor" 
                    value={state.vitrineAwningColor}
                    activeColorField={activeColorField}
                    toggleColorPicker={toggleColorPicker}
                    colorPickerRef={colorPickerRef}
                    handleColorChange={handleColorChange}
                  />
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Extras */}
      <div className="controlSection">
        <h3 onClick={() => setExtrasOpen(prev => !prev)} className="section-title">
          {extrasOpen ? '▾' : '▸'} Extras
        </h3>
        {extrasOpen && (
          <div className="button-grid">
            <ToggleButton 
              field="showLevelFloors" 
              onText="Hide Level Floors" 
              offText="Create Level Floors"
              handleToggle={handleToggle}
              isActive={state.showLevelFloors}
            />
            <ToggleButton 
              field="showLevelLines" 
              onText="Hide Level Lines" 
              offText="Show Level Lines"
              handleToggle={handleToggle}
              isActive={state.showLevelLines}
            />
            <ToggleButton 
              field="showEdges" 
              onText="Hide Edges" 
              offText="Show Wireframe"
              handleToggle={handleToggle}
              isActive={state.showEdges}
            />
            <ToggleButton 
              field="showBrickPattern" 
              onText="Hide Brick Pattern" 
              offText="Show Brick Pattern"
              handleToggle={handleToggle}
              isActive={state.showBrickPattern}
            />
            {state.showBrickPattern && (
              <>
                <div className="control-row" style={{ marginTop: '5px' }}>
                  <label className="control-label">Apply to:</label>
                  <div className="toggle-group">
                    <ToggleButton 
                      field="brickOnSideWalls" 
                      onText="Side Walls" 
                      offText="Side Walls"
                      handleToggle={handleToggle}
                      isActive={state.brickOnSideWalls}
                      className="small-toggle"
                    />
                    <ToggleButton 
                      field="brickOnFacade" 
                      onText="Facade" 
                      offText="Facade"
                      handleToggle={handleToggle}
                      isActive={state.brickOnFacade}
                      className="small-toggle"
                    />
                    <ToggleButton 
                      field="brickOnGroundFloor" 
                      onText="Ground Floor" 
                      offText="Ground Floor"
                      handleToggle={handleToggle}
                      isActive={state.brickOnGroundFloor}
                      className="small-toggle"
                    />
                  </div>
                </div>
                <div className="slider-control">
                  <label>Brick Contrast: {Number(state.brickContrast).toFixed(2)}</label>
                  <input
                    type="range"
                    min="-1"
                    max="1"
                    step="0.05"
                    value={state.brickContrast}
                    onChange={handleChange('brickContrast')}
                  />
                </div>
                <div className="slider-control">
                  <label>Section Erase: {Number(state.randomEraseBricks).toFixed(2)}</label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={state.randomEraseBricks}
                    onChange={handleChange('randomEraseBricks')}
                  />
                </div>
                <div className="slider-control">
                  <label>Fine Grain Erase: {Number(state.randomEraseFine).toFixed(2)}</label>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={state.randomEraseFine}
                    onChange={handleChange('randomEraseFine')}
                  />
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

export default BuildingControlsRight; 